import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Building2,
  Palette,
  Globe,
  Shield,
  Bell,
  Loader2,
  Save,
  Upload,
  Image as ImageIcon,
  Clock,
  Lock,
  Mail,
  Phone,
  Check,
  AlertTriangle,
  Gauge,
  Plus,
  Trash2,
  AlertCircle,
  Play,
  History,
  RefreshCw,
  CheckCircle,
  XCircle,
  Send,
  Smartphone,
  ExternalLink,
  Copy,
  FileText,
  Settings,
  RotateCcw,
  Users
} from 'lucide-react';
import { toast } from 'sonner';
import EscalationConfig from '../components/EscalationConfig';

import { API_BASE, BACKEND_URL } from '../utils/apiClient';

const API = API_BASE;

const TIMEZONES = [
  { value: 'America/Montreal', label: 'America/Montréal (UTC-5)' },
  { value: 'America/Toronto', label: 'America/Toronto (UTC-5)' },
  { value: 'America/New_York', label: 'America/New_York (UTC-5)' },
  { value: 'Europe/Paris', label: 'Europe/Paris (UTC+1)' },
  { value: 'Europe/London', label: 'Europe/London (UTC)' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (UTC+9)' },
  { value: 'Africa/Casablanca', label: 'Africa/Casablanca (UTC+1)' },
  { value: 'Africa/Dakar', label: 'Africa/Dakar (UTC)' }
];

const LANGUAGES = [
  { value: 'fr', label: 'Français' },
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' }
];

const DATE_FORMATS = [
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (31/12/2025)' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (12/31/2025)' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2025-12-31)' }
];

const CompanySettings = () => {
  const { getAuthHeaders, isAdmin, isDemoReadonly } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [activeTab, setActiveTab] = useState('identity');

  const [settings, setSettings] = useState({
    company_name: '',
    logo_url: null,
    primary_color: '#0E346E',
    timezone: 'America/Montreal',
    language: 'fr',
    date_format: 'DD/MM/YYYY',
    password_min_length: 8,
    password_require_uppercase: true,
    password_require_numbers: true,
    session_timeout_hours: 24,
    max_login_attempts: 5,
    two_factor_enabled: false,
    email_notifications_enabled: true,
    escalation_notifications_enabled: true,
    welcome_message: '',
    support_email: '',
    support_phone: '',
    work_shifts: ['Jour', 'Soir', 'Nuit'],
    trs_objective_global: 85,
    // Paramètres Tournées Gemba
    required_tours_per_day: 2,
    shifts_enabled: ['Jour', 'Soir', 'Nuit'],
    // Configuration avancée des quarts (nouveaux)
    shift_duration: '8h',
    shift_custom_duration_hours: null,
    // Limite de tournées (nouveaux)
    max_tours_per_day: 4,
    allow_unlimited_tours: false,
    // Threshold settings
    trs_threshold: 85,
    lean_threshold: 80,
    max_major_deviations: 2
  });
  const [newShift, setNewShift] = useState('');
  const [thresholds, setThresholds] = useState({
    trs_threshold: 85,
    lean_threshold: 80,
    max_major_deviations: 2
  });
  
  // Escalation state
  const [runningEscalation, setRunningEscalation] = useState(false);
  const [escalationResult, setEscalationResult] = useState(null);
  const [escalationHistory, setEscalationHistory] = useState([]);
  
  // Push notifications state
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  
  // Email config state
  const [emailConfig, setEmailConfig] = useState(null);
  const [loadingEmailConfig, setLoadingEmailConfig] = useState(false);
  
  // NEW: Escalation rules & roles state
  const [escalationRoles, setEscalationRoles] = useState([]);
  const [escalationRules, setEscalationRules] = useState([]);
  const [senderConfig, setSenderConfig] = useState({ sender_email: '', sender_name: '' });
  const [loadingEscalation, setLoadingEscalation] = useState(false);
  const [savingEscalation, setSavingEscalation] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [newRoleName, setNewRoleName] = useState('');
  
  // Settings history state
  const [settingsHistory, setSettingsHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [restoringVersion, setRestoringVersion] = useState(null);

  useEffect(() => {
    fetchSettings();
    fetchThresholds();
    fetchEscalationHistory();
    checkPushSupport();
    fetchEmailConfig();
    fetchEscalationConfig();
    fetchSettingsHistory();
  }, []);

  const fetchThresholds = async () => {
    try {
      const response = await axios.get(`${API}/settings/thresholds`, { headers: getAuthHeaders() });
      const d = response.data || {};
      setThresholds({
        trs_threshold: d.trs_threshold ?? 85,
        lean_threshold: d.lean_threshold ?? 80,
        max_major_deviations: d.max_major_deviations ?? 2
      });
    } catch (error) {
      console.error('Error fetching thresholds:', error);
    }
  };

  const handleSaveThresholds = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/thresholds`, thresholds, { headers: getAuthHeaders() });
      // garde la page cohérente : le bouton « Enregistrer » général ne doit pas écraser ces seuils
      setSettings(prev => ({ ...prev, ...thresholds }));
      toast.success('Seuils mis à jour avec succès');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde des seuils');
    } finally {
      setSaving(false);
    }
  };

  const fetchEscalationHistory = async () => {
    try {
      const response = await axios.get(`${API}/settings/escalation-history`, { 
        headers: getAuthHeaders(),
        params: { limit: 10 }
      });
      setEscalationHistory(response.data.history || []);
    } catch (error) {
      console.error('Error fetching escalation history:', error);
    }
  };

  const handleTriggerEscalation = async () => {
    setRunningEscalation(true);
    setEscalationResult(null);
    try {
      const response = await axios.post(`${API}/settings/trigger-escalation-check`, {}, { 
        headers: getAuthHeaders() 
      });
      setEscalationResult(response.data);
      toast.success(`Vérification terminée: ${response.data.actions_escalated} action(s) escaladée(s)`);
      // Refresh history
      fetchEscalationHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la vérification des escalades');
      setEscalationResult({ status: 'error', error: error.response?.data?.detail || 'Erreur inconnue' });
    } finally {
      setRunningEscalation(false);
    }
  };

  // Push notification functions
  const checkPushSupport = async () => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setPushSupported(supported);
    
    if (supported) {
      setPushPermission(Notification.permission);
      
      // Check subscription status
      try {
        const response = await axios.get(`${API}/push/status`, { headers: getAuthHeaders() });
        setPushSubscribed(response.data.subscribed);
      } catch (error) {
        console.error('Error checking push status:', error);
      }
    }
  };

  const handleSubscribePush = async () => {
    setPushLoading(true);
    try {
      // Request permission
      const permission = await Notification.requestPermission();
      setPushPermission(permission);
      
      if (permission !== 'granted') {
        toast.error('Permission de notification refusée');
        setPushLoading(false);
        return;
      }
      
      // Register service worker
      const registration = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;
      
      // Get VAPID public key
      const vapidResponse = await axios.get(`${API}/push/vapid-public-key`);
      const vapidPublicKey = vapidResponse.data.publicKey;
      
      if (!vapidPublicKey) {
        toast.error('Les notifications push ne sont pas configurées sur le serveur');
        setPushLoading(false);
        return;
      }
      
      // Subscribe
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });
      
      // Send subscription to server
      await axios.post(`${API}/push/subscribe`, {
        subscription: subscription.toJSON()
      }, { headers: getAuthHeaders() });
      
      setPushSubscribed(true);
      toast.success('Notifications push activées !');
    } catch (error) {
      console.error('Push subscription error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'activation des notifications');
    } finally {
      setPushLoading(false);
    }
  };

  const handleUnsubscribePush = async () => {
    setPushLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
        await axios.post(`${API}/push/unsubscribe`, {
          endpoint: subscription.endpoint
        }, { headers: getAuthHeaders() });
      }
      
      setPushSubscribed(false);
      toast.success('Notifications push désactivées');
    } catch (error) {
      toast.error('Erreur lors de la désactivation');
    } finally {
      setPushLoading(false);
    }
  };

  const handleTestPush = async () => {
    try {
      await axios.post(`${API}/push/test`, {}, { headers: getAuthHeaders() });
      toast.success('Notification de test envoyée !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi du test');
    }
  };

  // Email config functions
  const fetchEmailConfig = async () => {
    setLoadingEmailConfig(true);
    try {
      const response = await axios.get(`${API}/settings/email-config`, { headers: getAuthHeaders() });
      setEmailConfig(response.data);
    } catch (error) {
      console.error('Error fetching email config:', error);
    } finally {
      setLoadingEmailConfig(false);
    }
  };

  // Helper function for VAPID key conversion
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  // NEW: Escalation configuration functions
  const fetchEscalationConfig = async () => {
    setLoadingEscalation(true);
    try {
      const [rolesRes, rulesRes, senderRes] = await Promise.all([
        axios.get(`${API}/settings/escalation/roles`, { headers: getAuthHeaders() }),
        axios.get(`${API}/settings/escalation/rules`, { headers: getAuthHeaders() }),
        axios.get(`${API}/settings/escalation/sender`, { headers: getAuthHeaders() })
      ]);
      setEscalationRoles(rolesRes.data.roles || []);
      setEscalationRules(rulesRes.data.rules || []);
      setSenderConfig({
        sender_email: senderRes.data.sender_email || senderRes.data.default_email || '',
        sender_name: senderRes.data.sender_name || senderRes.data.default_name || ''
      });
    } catch (error) {
      console.error('Error fetching escalation config:', error);
    } finally {
      setLoadingEscalation(false);
    }
  };

  // Role management
  const handleAddRole = async () => {
    if (!newRoleName.trim()) {
      toast.error('Veuillez entrer un nom de rôle');
      return;
    }
    try {
      const response = await axios.post(`${API}/settings/escalation/roles`, 
        { name: newRoleName.trim() }, 
        { headers: getAuthHeaders() }
      );
      setEscalationRoles(prev => [...prev, response.data.role]);
      setNewRoleName('');
      toast.success('Rôle créé');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleUpdateRole = async (roleId, updates) => {
    try {
      await axios.put(`${API}/settings/escalation/roles/${roleId}`, updates, { headers: getAuthHeaders() });
      setEscalationRoles(prev => prev.map(r => r.id === roleId ? { ...r, ...updates } : r));
      setEditingRole(null);
      toast.success('Rôle mis à jour');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('Supprimer ce rôle ? Il sera retiré de toutes les règles d\'escalade.')) return;
    try {
      await axios.delete(`${API}/settings/escalation/roles/${roleId}`, { headers: getAuthHeaders() });
      setEscalationRoles(prev => prev.filter(r => r.id !== roleId));
      // Also update rules to remove this role
      setEscalationRules(prev => prev.map(rule => ({
        ...rule,
        role_ids: rule.role_ids.filter(id => id !== roleId)
      })));
      toast.success('Rôle supprimé');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  // Rule management
  const handleAddRule = async () => {
    try {
      const response = await axios.post(`${API}/settings/escalation/rules`, 
        { delay_days: 1, role_ids: [], email_addresses: [], is_active: true }, 
        { headers: getAuthHeaders() }
      );
      setEscalationRules(prev => [...prev, response.data.rule].sort((a, b) => a.delay_days - b.delay_days));
      toast.success('Règle créée');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleUpdateRule = async (ruleId, updates) => {
    try {
      await axios.put(`${API}/settings/escalation/rules/${ruleId}`, updates, { headers: getAuthHeaders() });
      setEscalationRules(prev => prev.map(r => r.id === ruleId ? { ...r, ...updates } : r).sort((a, b) => a.delay_days - b.delay_days));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Supprimer cette règle d\'escalade ?')) return;
    try {
      await axios.delete(`${API}/settings/escalation/rules/${ruleId}`, { headers: getAuthHeaders() });
      setEscalationRules(prev => prev.filter(r => r.id !== ruleId));
      toast.success('Règle supprimée');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleToggleRoleInRule = (ruleId, roleId) => {
    const rule = escalationRules.find(r => r.id === ruleId);
    if (!rule) return;
    
    const newRoleIds = rule.role_ids.includes(roleId)
      ? rule.role_ids.filter(id => id !== roleId)
      : [...rule.role_ids, roleId];
    
    handleUpdateRule(ruleId, { role_ids: newRoleIds });
  };

  const handleAddEmailToRule = (ruleId, email) => {
    if (!email || !email.includes('@')) {
      toast.error('Veuillez entrer un email valide');
      return;
    }
    const rule = escalationRules.find(r => r.id === ruleId);
    if (!rule) return;
    
    if (rule.email_addresses.includes(email)) {
      toast.error('Cet email est déjà dans la liste');
      return;
    }
    
    handleUpdateRule(ruleId, { email_addresses: [...rule.email_addresses, email] });
  };

  const handleRemoveEmailFromRule = (ruleId, email) => {
    const rule = escalationRules.find(r => r.id === ruleId);
    if (!rule) return;
    handleUpdateRule(ruleId, { email_addresses: rule.email_addresses.filter(e => e !== email) });
  };

  // Sender configuration
  const handleSaveSender = async () => {
    setSavingEscalation(true);
    try {
      await axios.put(`${API}/settings/escalation/sender`, senderConfig, { headers: getAuthHeaders() });
      toast.success('Expéditeur mis à jour');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingEscalation(false);
    }
  };

  const getRoleName = (roleId) => {
    const role = escalationRoles.find(r => r.id === roleId);
    return role ? role.name : roleId;
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/company`, { headers: getAuthHeaders() });
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/company`, settings, { headers: getAuthHeaders() });
      toast.success('Paramètres enregistrés avec succès');
      // Refresh history after save
      fetchSettingsHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const fetchSettingsHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await axios.get(`${API}/settings/company/history`, { headers: getAuthHeaders() });
      setSettingsHistory(response.data.history || []);
    } catch (error) {
      console.error('Error fetching settings history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleRestoreVersion = async (historyId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir restaurer cette version ? Les paramètres actuels seront remplacés.')) {
      return;
    }
    
    setRestoringVersion(historyId);
    try {
      const response = await axios.post(`${API}/settings/company/restore/${historyId}`, {}, { headers: getAuthHeaders() });
      toast.success('Paramètres restaurés avec succès');
      // Refresh settings and history
      fetchSettings();
      fetchSettingsHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la restauration');
    } finally {
      setRestoringVersion(null);
    }
  };

  const formatFieldName = (field) => {
    const fieldLabels = {
      company_name: 'Nom de l\'entreprise',
      logo_url: 'Logo',
      primary_color: 'Couleur principale',
      timezone: 'Fuseau horaire',
      language: 'Langue',
      date_format: 'Format de date',
      shifts_enabled: 'Quarts actifs',
      shift_duration: 'Durée des quarts',
      shift_custom_duration_hours: 'Durée personnalisée',
      max_tours_per_day: 'Limite de tournées/jour',
      allow_unlimited_tours: 'Tournées illimitées',
      required_tours_per_day: 'Tournées requises/jour',
      work_shifts: 'Quarts de travail',
      trs_objective_global: 'Objectif TRS global',
      password_min_length: 'Longueur min. mot de passe',
      email_notifications_enabled: 'Notifications email',
      escalation_notifications_enabled: 'Notifications escalade'
    };
    return fieldLabels[field] || field;
  };

  const formatFieldValue = (value) => {
    if (value === null || value === undefined) return '(vide)';
    if (typeof value === 'boolean') return value ? 'Oui' : 'Non';
    if (Array.isArray(value)) return value.join(', ');
    return String(value);
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Le fichier est trop volumineux (max 2 Mo)');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/settings/company/logo`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });

      setSettings(prev => ({ ...prev, logo_url: response.data.logo_url }));
      toast.success('Logo mis à jour');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du téléchargement');
    } finally {
      setUploadingLogo(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  // Tous les quarts connus : les trois standards + les quarts personnalisés de l'entreprise
  const allShifts = [...new Set(['Jour', 'Soir', 'Nuit', ...(settings.work_shifts || [])])];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!isAdmin()) {
    return (
      <div className="p-8 text-center">
        <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-slate-900">Accès restreint</h1>
        <p className="text-slate-500 mt-2">Seuls les administrateurs peuvent accéder aux paramètres entreprise.</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 space-y-6 fade-in" data-testid="company-settings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Barlow_Condensed']">
            PARAMÈTRES ENTREPRISE
          </h1>
          <p className="text-slate-500 mt-1">Configurez votre environnement GembaCoach</p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving || isDemoReadonly()}
          className="bg-[#0E346E] hover:bg-[#0A2850]"
          data-testid="save-settings-btn"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Enregistrer
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex w-full max-w-5xl overflow-x-auto">
          <TabsTrigger value="identity" className="flex items-center gap-2 shrink-0">
            <Building2 className="w-4 h-4" />
            <span className="hidden sm:inline">Identité</span>
          </TabsTrigger>
          <TabsTrigger value="localization" className="flex items-center gap-2 shrink-0">
            <Globe className="w-4 h-4" />
            <span className="hidden sm:inline">Localisation</span>
          </TabsTrigger>
          <TabsTrigger value="production" className="flex items-center gap-2 shrink-0">
            <Gauge className="w-4 h-4" />
            <span className="hidden sm:inline">Production</span>
          </TabsTrigger>
          <TabsTrigger value="tournees" className="flex items-center gap-2 shrink-0">
            <Clock className="w-4 h-4" />
            <span className="hidden sm:inline">Tournées</span>
          </TabsTrigger>
          <TabsTrigger value="thresholds" className="flex items-center gap-2 shrink-0">
            <AlertTriangle className="w-4 h-4" />
            <span className="hidden sm:inline">Seuils</span>
          </TabsTrigger>
          <TabsTrigger value="escalations" className="flex items-center gap-2 shrink-0">
            <AlertCircle className="w-4 h-4" />
            <span className="hidden sm:inline">Escalades</span>
          </TabsTrigger>
          <TabsTrigger value="security" className="flex items-center gap-2 shrink-0">
            <Shield className="w-4 h-4" />
            <span className="hidden sm:inline">Sécurité</span>
          </TabsTrigger>
          <TabsTrigger value="notifications" className="flex items-center gap-2 shrink-0">
            <Bell className="w-4 h-4" />
            <span className="hidden sm:inline">Notifications</span>
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2 shrink-0">
            <History className="w-4 h-4" />
            <span className="hidden sm:inline">Historique</span>
          </TabsTrigger>
        </TabsList>

        {/* Identity Tab */}
        <TabsContent value="identity" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-blue-600" />
                  Informations de l'entreprise
                </CardTitle>
                <CardDescription>
                  Personnalisez l'apparence de votre espace GembaCoach
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="company-name" className="font-semibold">Nom de l'entreprise</Label>
                  <Input
                    id="company-name"
                    value={settings.company_name || ''}
                    onChange={(e) => updateSetting('company_name', e.target.value)}
                    placeholder="Entrez le nom de votre entreprise"
                    className="border-2"
                    data-testid="company-name-input"
                  />
                  {!settings.company_name && (
                    <p className="text-xs text-amber-600 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      Ce champ est vide. Saisissez le nom de votre entreprise.
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="font-semibold">Logo de l'entreprise</Label>
                  <div className="flex items-center gap-4">
                    <div className="w-24 h-24 border-2 border-dashed rounded-lg flex items-center justify-center bg-slate-50">
                      {settings.logo_url ? (
                        <img 
                          src={`${BACKEND_URL}${settings.logo_url}`} 
                          alt="Logo" 
                          className="max-w-full max-h-full object-contain"
                        />
                      ) : (
                        <ImageIcon className="w-8 h-8 text-slate-300" />
                      )}
                    </div>
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                        id="logo-upload"
                      />
                      <label htmlFor="logo-upload">
                        <Button 
                          variant="outline" 
                          className="cursor-pointer" 
                          disabled={uploadingLogo}
                          asChild
                        >
                          <span>
                            {uploadingLogo ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Upload className="w-4 h-4 mr-2" />
                            )}
                            Télécharger logo
                          </span>
                        </Button>
                      </label>
                      <p className="text-xs text-slate-500 mt-1">PNG, JPEG ou SVG (max 2 Mo)</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="font-semibold flex items-center gap-2">
                    <Palette className="w-4 h-4" />
                    Couleur principale
                  </Label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={settings.primary_color}
                      onChange={(e) => updateSetting('primary_color', e.target.value)}
                      className="w-12 h-10 rounded cursor-pointer border-2"
                    />
                    <Input
                      value={settings.primary_color}
                      onChange={(e) => updateSetting('primary_color', e.target.value)}
                      className="w-32 border-2 font-mono"
                      maxLength={7}
                    />
                    <div 
                      className="h-10 flex-1 rounded-lg flex items-center justify-center text-white text-sm font-medium"
                      style={{ backgroundColor: settings.primary_color }}
                    >
                      Aperçu couleur
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-green-600" />
                  Contact & Support
                </CardTitle>
                <CardDescription>
                  Informations de contact affichées aux utilisateurs
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="support-email" className="font-semibold">Email support</Label>
                  <Input
                    id="support-email"
                    type="email"
                    value={settings.support_email || ''}
                    onChange={(e) => updateSetting('support_email', e.target.value)}
                    placeholder="support@entreprise.com"
                    className="border-2"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="support-phone" className="font-semibold">Téléphone support</Label>
                  <Input
                    id="support-phone"
                    type="tel"
                    value={settings.support_phone || ''}
                    onChange={(e) => updateSetting('support_phone', e.target.value)}
                    placeholder="+33 1 23 45 67 89"
                    className="border-2"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="welcome-message" className="font-semibold">Message de bienvenue</Label>
                  <textarea
                    id="welcome-message"
                    value={settings.welcome_message || ''}
                    onChange={(e) => updateSetting('welcome_message', e.target.value)}
                    placeholder="Bienvenue sur votre espace GembaCoach..."
                    className="w-full h-24 p-3 border-2 rounded-lg resize-none"
                  />
                  <p className="text-xs text-slate-500">Affiché sur la page de connexion</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Localization Tab */}
        <TabsContent value="localization" className="mt-6">
          <Card className="max-w-2xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-purple-600" />
                Paramètres régionaux
              </CardTitle>
              <CardDescription>
                Configurez la langue, le fuseau horaire et le format des dates
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="font-semibold">Langue par défaut</Label>
                  <Select value={settings.language} onValueChange={(v) => updateSetting('language', v)}>
                    <SelectTrigger className="border-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LANGUAGES.map(lang => (
                        <SelectItem key={lang.value} value={lang.value}>{lang.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="font-semibold">Fuseau horaire</Label>
                  <Select value={settings.timezone} onValueChange={(v) => updateSetting('timezone', v)}>
                    <SelectTrigger className="border-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMEZONES.map(tz => (
                        <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="font-semibold">Format de date</Label>
                <Select value={settings.date_format} onValueChange={(v) => updateSetting('date_format', v)}>
                  <SelectTrigger className="border-2 max-w-md">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DATE_FORMATS.map(df => (
                      <SelectItem key={df.value} value={df.value}>{df.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Production Tab - TRS Configuration */}
        <TabsContent value="production" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Configuration des Quarts */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-purple-600" />
                  Configuration des Quarts
                </CardTitle>
                <CardDescription>
                  Configurez la durée et les quarts de travail pour votre entreprise
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Durée des quarts */}
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <Label className="font-semibold text-purple-900 flex items-center gap-2 mb-3">
                    <Clock className="w-4 h-4" />
                    Durée des quarts
                  </Label>
                  <div className="grid grid-cols-4 gap-2 mb-3">
                    {['8h', '10h', '12h', 'custom'].map((duration) => (
                      <Button
                        key={duration}
                        variant={settings.shift_duration === duration ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => updateSetting('shift_duration', duration)}
                        className={settings.shift_duration === duration ? 'bg-purple-600 hover:bg-purple-700' : ''}
                        data-testid={`shift-duration-${duration}`}
                      >
                        {duration === 'custom' ? 'Personnalisé' : duration}
                      </Button>
                    ))}
                  </div>
                  {settings.shift_duration === 'custom' && (
                    <div className="flex items-center gap-2 mt-3">
                      <Label className="text-sm">Durée personnalisée:</Label>
                      <Input
                        type="number"
                        min="1"
                        max="24"
                        step="0.5"
                        value={settings.shift_custom_duration_hours || ''}
                        onChange={(e) => updateSetting('shift_custom_duration_hours', parseFloat(e.target.value) || null)}
                        className="w-20"
                        placeholder="Ex: 9"
                        data-testid="shift-custom-duration"
                      />
                      <span className="text-slate-500">heures</span>
                    </div>
                  )}
                </div>

                {/* Quarts actifs */}
                <div>
                  <Label className="font-semibold text-slate-700 flex items-center gap-2 mb-3">
                    <Settings className="w-4 h-4" />
                    Quarts actifs pour votre entreprise
                  </Label>
                  <p className="text-xs text-slate-500 mb-3">
                    Activez uniquement les quarts utilisés par votre entreprise. Les quarts désactivés ne seront pas proposés aux superviseurs.
                  </p>
                  <div className="space-y-2">
                    {allShifts.map((shift) => {
                      const isActive = (settings.shifts_enabled || ['Jour', 'Soir', 'Nuit']).includes(shift);
                      const canDisable = (settings.shifts_enabled || ['Jour', 'Soir', 'Nuit']).length > 1;
                      return (
                        <div 
                          key={shift} 
                          className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                            isActive ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-3 h-3 rounded-full ${isActive ? 'bg-green-500' : 'bg-slate-300'}`} />
                            <span className={`font-medium ${isActive ? 'text-green-700' : 'text-slate-500'}`}>
                              {shift}
                            </span>
                            {!isActive && <Badge variant="outline" className="text-xs">Désactivé</Badge>}
                          </div>
                          <Button
                            variant={isActive ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => {
                              const current = settings.shifts_enabled || ['Jour', 'Soir', 'Nuit'];
                              if (isActive) {
                                if (canDisable) {
                                  updateSetting('shifts_enabled', current.filter(s => s !== shift));
                                }
                              } else {
                                updateSetting('shifts_enabled', [...current, shift]);
                              }
                            }}
                            disabled={isActive && !canDisable}
                            className={isActive ? 'bg-green-600 hover:bg-green-700' : ''}
                            data-testid={`toggle-shift-${shift.toLowerCase()}`}
                          >
                            {isActive ? 'Actif' : 'Activer'}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Quarts personnalisés pour TRS */}
                <div className="border-t pt-4">
                  <Label className="font-medium text-slate-700 mb-2 block">
                    Quarts personnalisés (optionnel)
                  </Label>
                  <div className="space-y-2 mb-3">
                    {(settings.work_shifts || []).filter(s => !['Jour', 'Soir', 'Nuit'].includes(s)).map((shift, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-slate-50 rounded border">
                        <span className="text-sm text-slate-700">{shift}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7"
                          onClick={() => {
                            const newShifts = settings.work_shifts.filter(s => s !== shift);
                            const actifs = (settings.shifts_enabled || ['Jour', 'Soir', 'Nuit']).filter(s => s !== shift);
                            setSettings(prev => ({ ...prev, work_shifts: newShifts, shifts_enabled: actifs.length ? actifs : ['Jour'] }));
                          }}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Ex: Nuit 2, Relève..."
                      value={newShift}
                      onChange={(e) => setNewShift(e.target.value)}
                      className="flex-1 h-9"
                      data-testid="new-shift-input"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (newShift.trim() && !settings.work_shifts?.includes(newShift.trim())) {
                          const nom = newShift.trim();
                          // Un quart ajouté est actif d'emblée : il est proposé aux superviseurs
                          setSettings(prev => ({ ...prev, work_shifts: [...(prev.work_shifts || []), nom],
                            shifts_enabled: [...new Set([...(prev.shifts_enabled || ['Jour', 'Soir', 'Nuit']), nom])] }));
                          setNewShift('');
                        }
                      }}
                      disabled={!newShift.trim()}
                      data-testid="add-shift-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Ajouter
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Gauge className="w-5 h-5 text-purple-600" />
                  Configuration TRS par défaut
                </CardTitle>
                <CardDescription>
                  Paramètres par défaut pour le calcul du TRS (OEE)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Objectif TRS Global */}
                <div className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-200">
                  <Label className="font-semibold text-purple-900 flex items-center gap-2 mb-3">
                    <Gauge className="w-4 h-4" />
                    Objectif TRS Global (%)
                  </Label>
                  <div className="flex items-center gap-4">
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      value={settings.trs_objective_global || 85}
                      onChange={(e) => updateSetting('trs_objective_global', parseFloat(e.target.value) || 85)}
                      className="w-24 text-lg font-bold text-center border-2 border-purple-300"
                      data-testid="trs-objective-global"
                    />
                    <span className="text-purple-700 font-medium">%</span>
                    <div className="flex-1 text-sm text-purple-600">
                      Si le TRS d'une machine est inférieur à cet objectif, une action corrective sera créée automatiquement.
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
                  <h4 className="font-medium text-purple-900 mb-2">Formule TRS</h4>
                  <p className="text-sm text-purple-700 mb-3">
                    TRS = Disponibilité × Performance × Qualité
                  </p>
                  <div className="space-y-1 text-xs text-purple-600">
                    <p>• <strong>Disponibilité</strong> = (Temps planifié - Arrêts) / Temps planifié</p>
                    <p>• <strong>Performance</strong> = Production réelle / Production théorique</p>
                    <p>• <strong>Qualité</strong> = Production conforme / Production totale</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tournées Tab - Configuration des tournées Gemba */}
        <TabsContent value="tournees" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-l-4 border-l-blue-500">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-blue-600" />
                  Tournées quotidiennes
                </CardTitle>
                <CardDescription>
                  Configurez le nombre de tournées requises et la limite maximale par jour
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Nombre de tournées requises par jour</Label>
                  <Select 
                    value={String(settings.required_tours_per_day || 2)} 
                    onValueChange={(v) => setSettings({...settings, required_tours_per_day: parseInt(v)})}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 tournée par jour</SelectItem>
                      <SelectItem value="2">2 tournées par jour (recommandé)</SelectItem>
                      <SelectItem value="3">3 tournées par jour</SelectItem>
                      <SelectItem value="4">4 tournées par jour</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    Les superviseurs devront effectuer ce nombre de tournées minimum par jour
                  </p>
                </div>

                {/* Nouvelle section: Limite max de tournées */}
                <div className="space-y-3 pt-4 border-t">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <Settings className="w-4 h-4 text-blue-600" />
                    Limite maximale de tournées par jour
                  </Label>
                  <div className="flex items-center gap-4">
                    <Select 
                      value={String(settings.max_tours_per_day || 4)} 
                      onValueChange={(v) => setSettings({...settings, max_tours_per_day: parseInt(v)})}
                      disabled={settings.allow_unlimited_tours}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="2">2 max</SelectItem>
                        <SelectItem value="4">4 max</SelectItem>
                        <SelectItem value="6">6 max</SelectItem>
                        <SelectItem value="8">8 max</SelectItem>
                        <SelectItem value="10">10 max</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={settings.allow_unlimited_tours || false}
                        onCheckedChange={(checked) => setSettings({...settings, allow_unlimited_tours: checked})}
                        data-testid="allow-unlimited-tours"
                      />
                      <Label className="text-sm cursor-pointer">Illimité</Label>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500">
                    {settings.allow_unlimited_tours 
                      ? "Les superviseurs peuvent créer autant de tournées qu'ils le souhaitent."
                      : `Les superviseurs ne peuvent pas créer plus de ${settings.max_tours_per_day || 4} tournées par jour.`
                    }
                  </p>
                </div>

                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-2">Comment ça fonctionne ?</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Chaque tournée est numérotée (Tournée 1, 2, 3...)</li>
                    <li>• Le système suggère automatiquement le prochain rang</li>
                    <li>• Le Dashboard affiche la conformité de chaque superviseur</li>
                    <li>• Indicateur vert = objectif atteint, orange = en cours</li>
                    <li>• Bouton "Ajouter une tournée" visible si la limite n'est pas atteinte</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-purple-500">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Gauge className="w-5 h-5 text-purple-600" />
                  Quarts actifs pour les tournées
                </CardTitle>
                <CardDescription>
                  Les quarts désactivés ne seront pas proposés aux superviseurs lors de la création de tournées
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  {allShifts.map(shift => {
                    const isActive = (settings.shifts_enabled || ['Jour', 'Soir', 'Nuit']).includes(shift);
                    const canDisable = (settings.shifts_enabled || ['Jour', 'Soir', 'Nuit']).length > 1;
                    return (
                      <div 
                        key={shift} 
                        className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                          isActive ? 'bg-green-50 border border-green-200' : 'bg-slate-50 border border-slate-200'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${isActive ? 'bg-green-500' : 'bg-slate-300'}`} />
                          <span className={`font-medium ${isActive ? 'text-green-700' : 'text-slate-500'}`}>{shift}</span>
                        </div>
                        <Switch
                          checked={isActive}
                          onCheckedChange={(checked) => {
                            const currentShifts = settings.shifts_enabled || ['Jour', 'Soir', 'Nuit'];
                            if (checked) {
                              setSettings({...settings, shifts_enabled: [...currentShifts, shift]});
                            } else {
                              if (canDisable) {
                                setSettings({...settings, shifts_enabled: currentShifts.filter(s => s !== shift)});
                              } else {
                                toast.error('Au moins un quart doit être actif');
                              }
                            }
                          }}
                          data-testid={`shift-toggle-${shift.toLowerCase()}`}
                        />
                      </div>
                    );
                  })}
                </div>

                <div className="bg-purple-50 rounded-lg p-4">
                  <h4 className="font-medium text-purple-900 mb-2">Personnalisation</h4>
                  <p className="text-sm text-purple-800">
                    {(settings.shifts_enabled || ['Jour', 'Soir', 'Nuit']).length === 1 
                      ? `Seul le quart "${settings.shifts_enabled[0]}" sera proposé aux superviseurs.`
                      : `Les superviseurs pourront choisir parmi : ${(settings.shifts_enabled || ['Jour', 'Soir', 'Nuit']).join(', ')}.`
                    }
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Save Button for Tournées */}
          <div className="flex justify-end mt-6">
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer les paramètres
                </>
              )}
            </Button>
          </div>
        </TabsContent>

        {/* Thresholds Tab - NEW */}
        <TabsContent value="thresholds" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-l-4 border-l-amber-500">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  Seuils d'alerte
                </CardTitle>
                <CardDescription>
                  Définissez les seuils qui déclenchent des alertes lors de la clôture des tournées
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-3">
                  <Label className="font-semibold">Seuil TRS minimum (%)</Label>
                  <p className="text-sm text-slate-500">Une alerte sera générée si le TRS est inférieur à ce seuil</p>
                  <div className="flex items-center gap-4">
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={thresholds.trs_threshold}
                      onChange={(e) => setThresholds({...thresholds, trs_threshold: parseFloat(e.target.value)})}
                      className="w-24 border-2"
                    />
                    <span className="text-slate-600">%</span>
                    <div className="flex-1 bg-slate-100 h-2 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-amber-500 transition-all"
                        style={{ width: `${thresholds.trs_threshold}%` }}
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <Label className="font-semibold">Seuil Score Lean minimum (%)</Label>
                  <p className="text-sm text-slate-500">Une alerte sera générée si le score Lean est inférieur à ce seuil</p>
                  <div className="flex items-center gap-4">
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={thresholds.lean_threshold}
                      onChange={(e) => setThresholds({...thresholds, lean_threshold: parseFloat(e.target.value)})}
                      className="w-24 border-2"
                    />
                    <span className="text-slate-600">%</span>
                    <div className="flex-1 bg-slate-100 h-2 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-green-500 transition-all"
                        style={{ width: `${thresholds.lean_threshold}%` }}
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <Label className="font-semibold">Nombre maximum d'écarts majeurs</Label>
                  <p className="text-sm text-slate-500">Une alerte sera générée si le nombre de non-conformités dépasse ce seuil</p>
                  <div className="flex items-center gap-4">
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={thresholds.max_major_deviations}
                      onChange={(e) => setThresholds({...thresholds, max_major_deviations: parseInt(e.target.value)})}
                      className="w-24 border-2"
                    />
                    <span className="text-slate-600">écarts</span>
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <Button
                    onClick={handleSaveThresholds}
                    disabled={saving}
                    className="w-full bg-amber-600 hover:bg-amber-700"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Enregistrement...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Enregistrer les seuils
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-600" />
                  Comportement des alertes
                </CardTitle>
                <CardDescription>
                  Ce qui se passe quand les seuils ne sont pas atteints
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-amber-50 border-l-4 border-l-amber-500 rounded-r-lg">
                  <h4 className="font-semibold text-amber-800">Lors de la clôture d'une tournée</h4>
                  <ul className="mt-2 space-y-2 text-sm text-amber-700">
                    <li>• La tournée est marquée "À analyser"</li>
                    <li>• Le supérieur hiérarchique est notifié</li>
                    <li>• Un email automatique est envoyé</li>
                    <li>• Une notification prioritaire apparaît dans le dashboard</li>
                  </ul>
                </div>

                <div className="p-4 bg-blue-50 border-l-4 border-l-blue-500 rounded-r-lg">
                  <h4 className="font-semibold text-blue-800">Hiérarchie de notification</h4>
                  <ul className="mt-2 space-y-2 text-sm text-blue-700">
                    <li>• Superviseur → Directeur de Production</li>
                    <li>• Configurez la hiérarchie dans "Utilisateurs"</li>
                  </ul>
                </div>

                <div className="p-4 bg-slate-50 border-l-4 border-l-slate-400 rounded-r-lg">
                  <h4 className="font-semibold text-slate-700">Traçabilité</h4>
                  <ul className="mt-2 space-y-2 text-sm text-slate-600">
                    <li>• Toute modification de tournée verrouillée nécessite une justification</li>
                    <li>• Un journal d'audit est maintenu automatiquement</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Escalations Tab - NEW */}
        <TabsContent value="escalations" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Trigger Escalation Card */}
            <Card className="border-l-4 border-l-red-500">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                  Vérification des escalades
                </CardTitle>
                <CardDescription>
                  Lancez manuellement la vérification et l'escalade des actions en retard
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <h4 className="font-semibold text-red-900 mb-2">Règles d'escalade</h4>
                  <ul className="text-sm text-red-800 space-y-1">
                    <li>• <strong>J+1 (Niveau 1)</strong> : Action en retard de 1 jour → Notification au responsable</li>
                    <li>• <strong>J+3 (Niveau 2)</strong> : Action en retard de 3 jours → Notification au responsable + manager</li>
                    <li>• <strong>J+7 (Niveau 3 - CRITIQUE)</strong> : Action en retard de 7+ jours → Notification à tous les admins</li>
                  </ul>
                </div>

                <Button
                  onClick={handleTriggerEscalation}
                  disabled={runningEscalation}
                  className="w-full bg-red-600 hover:bg-red-700 text-white"
                  data-testid="trigger-escalation-btn"
                >
                  {runningEscalation ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Vérification en cours...
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4 mr-2" />
                      Lancer la vérification des escalades
                    </>
                  )}
                </Button>

                {/* Result Display */}
                {escalationResult && (
                  <div className={`p-4 rounded-lg border ${
                    escalationResult.status === 'completed' 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-red-50 border-red-200'
                  }`}>
                    <div className="flex items-center gap-2 mb-2">
                      {escalationResult.status === 'completed' ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-600" />
                      )}
                      <span className={`font-semibold ${
                        escalationResult.status === 'completed' ? 'text-green-900' : 'text-red-900'
                      }`}>
                        {escalationResult.message || 'Résultat de la vérification'}
                      </span>
                    </div>
                    
                    {escalationResult.status === 'completed' && (
                      <div className="mt-3 grid grid-cols-2 gap-4">
                        <div className="text-center p-2 bg-white rounded">
                          <div className="text-2xl font-bold text-slate-800">{escalationResult.actions_checked}</div>
                          <div className="text-xs text-slate-500">Actions vérifiées</div>
                        </div>
                        <div className="text-center p-2 bg-white rounded">
                          <div className="text-2xl font-bold text-red-600">{escalationResult.actions_escalated}</div>
                          <div className="text-xs text-slate-500">Actions escaladées</div>
                        </div>
                      </div>
                    )}

                    {escalationResult.results && escalationResult.results.length > 0 && (
                      <div className="mt-4 max-h-48 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-white/50">
                            <tr>
                              <th className="text-left p-1">Action</th>
                              <th className="text-left p-1">Statut</th>
                              <th className="text-left p-1">Niveau</th>
                            </tr>
                          </thead>
                          <tbody>
                            {escalationResult.results.filter(r => r.status === 'escalated').map((result, idx) => (
                              <tr key={idx} className="border-t border-green-200">
                                <td className="p-1 truncate max-w-[150px]" title={result.action_title}>
                                  {result.action_title || result.action_id?.slice(0, 8)}
                                </td>
                                <td className="p-1">
                                  <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-xs">
                                    Escaladée
                                  </span>
                                </td>
                                <td className="p-1 font-medium">{result.level_label}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                <div className="text-xs text-slate-500">
                  <p>Cette vérification sera exécutée automatiquement toutes les heures en production.</p>
                </div>
              </CardContent>
            </Card>

            {/* Escalation History Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="w-5 h-5 text-slate-600" />
                  Historique des vérifications
                </CardTitle>
                <CardDescription>
                  Dernières exécutions du job d'escalade
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex justify-end mb-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={fetchEscalationHistory}
                    data-testid="refresh-history-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-1" />
                    Actualiser
                  </Button>
                </div>

                {escalationHistory.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <History className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>Aucune exécution enregistrée</p>
                    <p className="text-xs mt-1">Lancez une vérification pour commencer</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {escalationHistory.map((job, idx) => (
                      <div 
                        key={job.id || idx} 
                        className={`p-3 rounded-lg border ${
                          job.status === 'success' 
                            ? 'bg-green-50 border-green-200' 
                            : 'bg-red-50 border-red-200'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {job.status === 'success' ? (
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            ) : (
                              <XCircle className="w-4 h-4 text-red-600" />
                            )}
                            <span className="font-medium text-sm">
                              {new Date(job.run_at).toLocaleString('fr-CA', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            job.status === 'success' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {job.status === 'success' ? 'Succès' : 'Échec'}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="text-slate-600">
                            <span className="font-medium">{job.actions_checked || 0}</span> actions vérifiées
                          </div>
                          <div className="text-red-600 font-medium">
                            <span className="font-bold">{job.actions_escalated || 0}</span> escaladées
                          </div>
                        </div>
                        {job.triggered_by_name && (
                          <div className="text-xs text-slate-400 mt-1">
                            Lancé par : {job.triggered_by_name}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="w-5 h-5 text-red-600" />
                  Politique de mots de passe
                </CardTitle>
                <CardDescription>
                  Configurez les exigences de sécurité des mots de passe
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="font-semibold">Longueur minimale</Label>
                  <Select 
                    value={settings.password_min_length.toString()} 
                    onValueChange={(v) => updateSetting('password_min_length', parseInt(v))}
                  >
                    <SelectTrigger className="border-2 w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[6, 8, 10, 12, 14, 16].map(n => (
                        <SelectItem key={n} value={n.toString()}>{n} caractères</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label className="font-semibold">Majuscules requises</Label>
                    <p className="text-sm text-slate-500">Au moins une lettre majuscule</p>
                  </div>
                  <Switch
                    checked={settings.password_require_uppercase}
                    onCheckedChange={(v) => updateSetting('password_require_uppercase', v)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label className="font-semibold">Chiffres requis</Label>
                    <p className="text-sm text-slate-500">Au moins un chiffre</p>
                  </div>
                  <Switch
                    checked={settings.password_require_numbers}
                    onCheckedChange={(v) => updateSetting('password_require_numbers', v)}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-amber-600" />
                  Session & Connexion
                </CardTitle>
                <CardDescription>
                  Configurez la durée des sessions et les tentatives de connexion
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="font-semibold">Durée de session</Label>
                  <Select 
                    value={settings.session_timeout_hours.toString()} 
                    onValueChange={(v) => updateSetting('session_timeout_hours', parseInt(v))}
                  >
                    <SelectTrigger className="border-2 w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 4, 8, 12, 24, 48, 72].map(n => (
                        <SelectItem key={n} value={n.toString()}>{n} heure{n > 1 ? 's' : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="font-semibold">Tentatives de connexion max</Label>
                  <Select 
                    value={settings.max_login_attempts.toString()} 
                    onValueChange={(v) => updateSetting('max_login_attempts', parseInt(v))}
                  >
                    <SelectTrigger className="border-2 w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[3, 5, 10, 15].map(n => (
                        <SelectItem key={n} value={n.toString()}>{n} tentatives</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">Compte bloqué temporairement après dépassement</p>
                </div>

                <div className="flex items-center justify-between pt-2">
                  <div>
                    <Label className="font-semibold">Authentification à deux facteurs</Label>
                    <p className="text-sm text-slate-500">Requiert un code SMS/Email</p>
                  </div>
                  <Switch
                    checked={settings.two_factor_enabled}
                    onCheckedChange={(v) => updateSetting('two_factor_enabled', v)}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="mt-6">
          <div className="space-y-6">
            {/* Global Email/Push Settings */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Email Notifications Switch */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="w-5 h-5 text-blue-600" />
                    Notifications Email
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div>
                      <Label className="font-semibold">Notifications email</Label>
                      <p className="text-sm text-slate-500">Activer/désactiver toutes les notifications</p>
                    </div>
                    <Switch
                      checked={settings.email_notifications_enabled}
                      onCheckedChange={(v) => updateSetting('email_notifications_enabled', v)}
                    />
                  </div>
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div>
                      <Label className="font-semibold">Notifications d'escalade</Label>
                      <p className="text-sm text-slate-500">Email lors de l'escalade d'actions en retard</p>
                    </div>
                    <Switch
                      checked={settings.escalation_notifications_enabled}
                      onCheckedChange={(v) => updateSetting('escalation_notifications_enabled', v)}
                      disabled={!settings.email_notifications_enabled}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Push Notifications */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Smartphone className="w-5 h-5 text-green-600" />
                    Notifications Push
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {!pushSupported ? (
                    <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="flex items-center gap-2 text-amber-700">
                        <AlertTriangle className="w-4 h-4" />
                        <span className="text-sm font-medium">Non supporté par ce navigateur</span>
                      </div>
                    </div>
                  ) : pushPermission === 'denied' ? (
                    <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                      <div className="flex items-center gap-2 text-red-700">
                        <XCircle className="w-4 h-4" />
                        <span className="text-sm font-medium">Permission refusée</span>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                        <div>
                          <Label className="font-semibold">Statut</Label>
                          <p className="text-sm text-slate-500">
                            {pushSubscribed ? 'Activées' : 'Désactivées'}
                          </p>
                        </div>
                        <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                          pushSubscribed ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'
                        }`}>
                          {pushSubscribed ? 'Actif' : 'Inactif'}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {pushSubscribed ? (
                          <>
                            <Button variant="outline" className="flex-1" onClick={handleTestPush}>
                              <Send className="w-4 h-4 mr-2" />
                              Tester
                            </Button>
                            <Button variant="destructive" className="flex-1" onClick={handleUnsubscribePush} disabled={pushLoading}>
                              {pushLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                              Désactiver
                            </Button>
                          </>
                        ) : (
                          <Button className="w-full bg-green-600 hover:bg-green-700" onClick={handleSubscribePush} disabled={pushLoading}>
                            {pushLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bell className="w-4 h-4 mr-2" />}
                            Activer les notifications push
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Escalation Configuration Component */}
            <EscalationConfig />
          </div>
        </TabsContent>

        {/* History Tab - NEW */}
        <TabsContent value="history" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="w-5 h-5 text-indigo-600" />
                Historique des modifications
              </CardTitle>
              <CardDescription>
                Consultez l'historique des changements de paramètres et restaurez une version précédente si nécessaire
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex justify-end mb-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchSettingsHistory}
                  disabled={loadingHistory}
                  data-testid="refresh-settings-history-btn"
                >
                  {loadingHistory ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-1" />
                  )}
                  Actualiser
                </Button>
              </div>

              {loadingHistory ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
                </div>
              ) : settingsHistory.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <History className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg font-medium">Aucun historique</p>
                  <p className="text-sm mt-1">Les modifications de paramètres seront enregistrées ici</p>
                </div>
              ) : (
                <div className="space-y-4 max-h-[600px] overflow-y-auto">
                  {settingsHistory.map((entry, idx) => (
                    <div 
                      key={entry.id || idx}
                      className={`p-4 rounded-lg border ${
                        entry.action === 'restore' 
                          ? 'bg-amber-50 border-amber-200' 
                          : 'bg-slate-50 border-slate-200'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            {entry.action === 'restore' ? (
                              <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300">
                                <RotateCcw className="w-3 h-3 mr-1" />
                                Restauration
                              </Badge>
                            ) : entry.action === 'create' ? (
                              <Badge variant="outline" className="bg-green-100 text-green-700 border-green-300">
                                <Plus className="w-3 h-3 mr-1" />
                                Création
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-300">
                                <FileText className="w-3 h-3 mr-1" />
                                Modification
                              </Badge>
                            )}
                            <span className="text-sm text-slate-600">
                              {new Date(entry.timestamp).toLocaleString('fr-FR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                          
                          <div className="text-sm text-slate-700 mb-2">
                            <span className="font-medium">{entry.user_name}</span>
                            {entry.action === 'restore' && entry.restored_from_date && (
                              <span className="text-amber-700">
                                {' '}a restauré la version du {new Date(entry.restored_from_date).toLocaleDateString('fr-FR')}
                              </span>
                            )}
                          </div>

                          {/* Changes list */}
                          {entry.changes && entry.changes.length > 0 && (
                            <div className="mt-2 space-y-1">
                              {entry.changes.slice(0, 5).map((change, cIdx) => (
                                <div key={cIdx} className="text-xs flex items-center gap-2 text-slate-600">
                                  <span className="font-medium">{formatFieldName(change.field)}:</span>
                                  <span className="text-red-600 line-through">{formatFieldValue(change.old_value)}</span>
                                  <span className="text-slate-400">→</span>
                                  <span className="text-green-600">{formatFieldValue(change.new_value)}</span>
                                </div>
                              ))}
                              {entry.changes.length > 5 && (
                                <div className="text-xs text-slate-500">
                                  ... et {entry.changes.length - 5} autre(s) modification(s)
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Restore button */}
                        {entry.snapshot && idx > 0 && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRestoreVersion(entry.id)}
                            disabled={restoringVersion === entry.id}
                            className="shrink-0"
                            data-testid={`restore-version-${entry.id}`}
                          >
                            {restoringVersion === entry.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <RotateCcw className="w-4 h-4 mr-1" />
                                Restaurer
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CompanySettings;
